import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  FETCH_DATA_TAG_ALL_FILES,
  FETCH_DATA_ESTATE_OVERVIEW,
  POP_UP_LOCATION_KEY,
  SET_TAG_FILES_SELECTION,
} from "../DataTagConstants";
import "./DataTagAllFilesTable.scss";
import { renderDataTagNames, renderDuplicateCounts } from "./DataTagUsersTable";
import FilterableHeader from "../Data Tag FIlters/FilterableHeader";
import DataTagFileSearch from "../Data Tag FIlters/DataTagFileSearch";
import { categoryIcons } from "../../../components/common/utilities";
import DataTagUserSearch from "../Data Tag FIlters/DataTagUserSearch";
import { Drawer, makeStyles } from "material-ui-core";
import RenderDataMapFilePopUp from "../../../components/shared/FilePopUp";
import { Tooltip, Checkbox } from "@material-ui/core";
import QuickFilters from "./QuickFilters";
import { sizeToBytes } from "../DataTagUtilities";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import DuplicateFileDrillDown from "./DuplicateFileDrillDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ErrorIcon from "@material-ui/icons/Error";

export const SENSITIVITY_LOOKUP = {
  CRITICAL: { label: "Critical", className: "tag-critical" },
  HIGH: { label: "High", className: "tag-high" },
  MEDIUM: { label: "Medium", className: "tag-medium" },
  LOW: { label: "Low", className: "tag-low" },
  NO_RISK: { label: "None", className: "tag-none" },
  NO_SENSITIVITY: { label: "Pending Classification", className: "tag-pending" },
  NOT_PROCESSED: { label: "Pending Classification", className: "tag-pending" },
};

export const CATEGORY_LOOKUP = {
  ORGANIZATIONAL: { label: "Organizational", className: "cat-organizational" },
  PERSONAL: { label: "Personal", className: "cat-personal" },
  API: { label: "API", className: "cat-api" },
  FINANCIAL: { label: "Financial", className: "cat-financial" },
  MARKETING: { label: "Marketing", className: "cat-marketing" },
  MEDICAL: { label: "Medical", className: "cat-medical" },
  OTHER: { label: "Other", className: "cat-other" },
};

export const TAG_STATUS_LOOKUP = {
  COMPLETED: { label: "Success", color: "#2e9e5b", Icon: CheckCircleIcon }, // green tick
  FAILED: { label: "Failed", color: "#d93025", Icon: CancelIcon }, // red cross
  INPROGRESS: { label: "In progress", color: "#f5a000", Icon: CheckCircleIcon }, // amber tick
  UNSUPPORTED: { label: "Unsupported", color: "#9aa0a6", Icon: ErrorIcon }, // grey circle with exclamation
};

const renderTagStatus = (value) => {
  // IN_PROGRESS / "in progress" / INPROGRESS all map to the same key
  const key = value ? String(value).toUpperCase().replace(/[^A-Z]/g, "") : "";
  const info = TAG_STATUS_LOOKUP[key];

  if (!info) {
    return (
      <Tooltip title="Not tagged">
        <span style={{ color: "#8f8f8f", fontWeight: 600 }}>-</span>
      </Tooltip>
    );
  }

  const { Icon, color, label } = info;
  return (
    <Tooltip title={label}>
      <span style={{ display: "inline-flex" }}>
        <Icon style={{ fontSize: 20, color }} />
      </span>
    </Tooltip>
  );
};

const useStyles = makeStyles({
  list: {
    width: 500,
    margin: "20px 20px",
  },
  fullList: {
    width: "auto",
  },
  paper: {
    background: "white",
  },
  paperReport: {
    background: "white",
    overflowY: "hidden",
  },
  "& .MuiFormControl": {
    width: "100%",
  },
});

const safe = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

export default function DataTagAllFilesTable() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [fileNameExpanded, setFileNameExpanded] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );

  const sourceName = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );

  const tableData = useSelector(
    (state) => state.dataTagReducer.allFiles.content || [],
  );

  const totalCount = useSelector(
    (state) => state.dataTagReducer.allFiles?.totalCount || 0,
  );

  const summaryDataRaw = useSelector(
    (state) => state.dataTagReducer.categoryCardData,
  );

  const dataMapFilePopUp = useSelector(
    (state) => state.dataMapReducer.dataMapFilePopUp,
  );

  const sensitiveCardData = useSelector(
    (state) => state.dataTagReducer.sensitiveCardData,
  );

  const classificationStats = sensitiveCardData?.fileClassificationStatus || [];

  const fileTypesList = useSelector(
    (state) => state.dataTagReducer.fileTypesList,
  );

  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase)

  // bumps after "Tag Files" succeeds so the table (and Tag Status) reloads
  const tagRefreshKey = useSelector((state) => state.dataTagReducer.tagRefreshKey);

  // "Tag Files" -> "Select files in table": show a checkbox column to pick files
  const isNTFS = sourceName === "NTFS";
  const tagSelectMode = useSelector((state) => state.dataTagReducer.tagFilesSelectMode);
  const tagSelection = useSelector((state) => state.dataTagReducer.tagFilesSelection);
  const selectMode = !!tagSelectMode && isNTFS && !isDataBase;
  const selectedFileIds = tagSelection?.fileIds || [];

  const setSelectedFileIds = (ids) =>
    dispatch({
      type: SET_TAG_FILES_SELECTION,
      payload: { ...tagSelection, fileIds: ids },
    });

  const toggleFileId = (id) =>
    setSelectedFileIds(
      selectedFileIds.includes(id)
        ? selectedFileIds.filter((x) => x !== id)
        : [...selectedFileIds, id],
    );

  const overviewLoaded = useSelector(
    (state) => !!state.dataTagReducer.dataEstateOverview?.card3,
  );

  useEffect(() => {
    if (dataSourceId && !overviewLoaded) {
      dispatch({ type: FETCH_DATA_ESTATE_OVERVIEW, payload: dataSourceId });
    }
  }, [dataSourceId]);


  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const [fileNameFilter, setFileNameFilter] = useState([]);
  const [usernameFilter, setUsernameFilter] = useState([]);
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [categoryFilter, setSelectedCategoryFilter] = useState([]);
  const [regulatedFilter, setSelectedRegulatedFilter] = useState([]);
  const [typeFilter, setFileTypeFilter] = useState([]);
  const [extensionFilter, setExtensionFilter] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    lastAccessTime: false, // OFF initially
  });

  const filters = useMemo(() => {
    const set = new Set();
    classificationStats.forEach((item) => {
      (item["FileClassificationStats.classificationCombination"] || "")
        .split(",")
        .forEach((c) => c.trim() && set.add(c.trim()));
    });
    return Array.from(set);
  }, [classificationStats]);

  const toggleFilter = (type) => {
    setSelectedRegulatedFilter((prev = []) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type],
    );
  };

  const handleFilePopUp = (data) => {
    if (selectMode) return; // rows are being ticked, not opened
    if (data.row?.sensitivity !== "NO_SENSITIVITY") {
      setSelectedFile(data.row);
      dispatch({ type: "OPEN_FILE_POP_UP", payload: data.row });
      dispatch({ type: POP_UP_LOCATION_KEY, payload: "dataTag" });
    }
  };

  useEffect(() => {
    if (!dataSourceId) return;

    dispatch({
      type: FETCH_DATA_TAG_ALL_FILES,
      payload: {
        dataSourceId,
        page: paginationModel.page,
        size: paginationModel.pageSize,
        fileNames: fileNameFilter.length > 0 ? fileNameFilter : null,
        usernames: usernameFilter.length > 0 ? usernameFilter : null,
        sensitivity:
          selectedSensitivity.length > 0 ? selectedSensitivity : null,
        category: categoryFilter.length > 0 ? categoryFilter : null,
        regulations: regulatedFilter.length > 0 ? regulatedFilter : null,
        types: typeFilter.length > 0 ? typeFilter : null,
        extensions: extensionFilter.length > 0 ? extensionFilter : null,
      },
    });
  }, [
    dataSourceId,
    paginationModel.page,
    paginationModel.pageSize,
    fileNameFilter,
    selectedSensitivity,
    usernameFilter,
    categoryFilter,
    regulatedFilter,
    typeFilter,
    extensionFilter,
    tagRefreshKey,
    dispatch,
  ]);

  const formatTime = (value) =>
    value
      ? `${dayjs(value).format("MM-DD-YYYY")} ${dayjs(value).format("HH:mm")}`
      : "-";

  const handlePaginationChange = (params) => {
    const newPagination = {
      pageSize: params.pageSize,
      page: params.page,
    };
    if (paginationModel.pageSize !== newPagination.pageSize) {
      newPagination.page = 0;
    }
    setPaginationModel(newPagination);
  };

 const pageFileIds = tableData.map((r) => r.fileId).filter(Boolean);
 const pageSelectedCount = pageFileIds.filter((id) =>
   selectedFileIds.includes(id),
 ).length;
 const allPageSelected =
   pageFileIds.length > 0 && pageSelectedCount === pageFileIds.length;

 const toggleAllOnPage = () =>
   setSelectedFileIds(
     allPageSelected
       ? selectedFileIds.filter((id) => !pageFileIds.includes(id))
       : Array.from(new Set([...selectedFileIds, ...pageFileIds])),
   );

 const columns = [
   ...(selectMode
     ? [
         {
           field: "__tagSelect",
           headerName: "",
           width: 52,
           sortable: false,
           disableColumnMenu: true,
           hideable: false,
           renderHeader: () => (
             <Checkbox
               color="primary"
               size="small"
               checked={allPageSelected}
               indeterminate={pageSelectedCount > 0 && !allPageSelected}
               onChange={toggleAllOnPage}
               onClick={(e) => e.stopPropagation()}
               disabled={pageFileIds.length === 0}
             />
           ),
           renderCell: (p) => (
             <Checkbox
               color="primary"
               size="small"
               checked={selectedFileIds.includes(p.row.fileId)}
               onChange={() => toggleFileId(p.row.fileId)}
               onClick={(e) => e.stopPropagation()}
             />
           ),
         },
       ]
     : []),
   {
     field: "fileName",
     headerName: isDataBase ? "Field" : "File Name",
     hideable: false,
     width: fileNameExpanded ? 600 : 350,
     minWidth: 250,
     maxWidth: 600,
     renderHeader: () => (
       <div className="filenameHeaderContainer">
         <FilterableHeader
           title={isDataBase ? "Field" : "File Name"}
           isFilterApplied={fileNameFilter.length > 0}
           onClear={() => setFileNameFilter([])}
         >
           <div>
             <DataTagFileSearch
               value={fileNameFilter}
               onChange={setFileNameFilter}
             />
           </div>
         </FilterableHeader>

         <button
           onClick={(e) => {
             e.stopPropagation();
             setFileNameExpanded((prev) => !prev);
           }}
           className="fileNameExpandIconButton"
           title={fileNameExpanded ? "Shrink column" : "Expand column"}
         >
           {fileNameExpanded ? (
             <AspectRatioIcon style={{ fontSize: 16, color: "#8f8f8f" }} />
           ) : (
             <ZoomOutMapIcon style={{ fontSize: 16, color: "#8f8f8f" }} />
           )}
         </button>
       </div>
     ),
     renderCell: (p) => renderDataTagNames(p.value, fileNameExpanded),
   },
   {
     field: "owner",
     headerName: isDataBase ? "Table" : "Owner",
     hideable: false,
     flex: 2,
     renderHeader: () => (
       <FilterableHeader
         title={isDataBase ? "Table" : "Owner"}
         isFilterApplied={usernameFilter.length > 0}
         onClear={() => setUsernameFilter([])}
       >
         <DataTagUserSearch
           selectedValues={usernameFilter}
           onSelectionChange={(selected) => setUsernameFilter(selected)}
         />
       </FilterableHeader>
     ),
     renderCell: (p) => renderDataTagNames(p.value),
   },
   {
     field: "sensitivity",
     headerName: "Sensitivity",
     hideable: false,
     flex: 1.2,
     renderCell: (p) => {
       if (!p.value) return "-";
       const info =
         SENSITIVITY_LOOKUP[p.value] || SENSITIVITY_LOOKUP.NO_SENSITIVITY;
       return (
         <span className={`tag-chip ${info.className}`}>{info.label}</span>
       );
     },
   },
   {
     field: "regulations",
     headerName: "Regulations",
     hideable: false,
     flex: 1.7,
     renderCell: (params) => {
       const values = Array.isArray(params.value) ? params.value : [];
       if (values.length === 0) return "-";

       const visible = fileNameExpanded
         ? values.slice(0, 1)
         : values.slice(0, 2);
       const remaining = fileNameExpanded ? values.slice(1) : values.slice(2);

       return (
         <div className="regulation-cell">
           {visible.map((item) => (
             <span key={item} className="regulation-pill">
               {item}
             </span>
           ))}
           {remaining.length > 0 && (
             <Tooltip
               title={
                 <div className="regulation-tooltip-content">
                   {remaining.map((item) => (
                     <div key={item} className="tooltip-row">
                       {item}
                     </div>
                   ))}
                 </div>
               }
               placement="top"
               arrow
             >
               <span className="regulation-more">+{remaining.length}</span>
             </Tooltip>
           )}
         </div>
       );
     },
   },
   {
     field: "category",
     headerName: "Category",
     hideable: false,
     flex: 1.2,
     renderCell: (params) => {
       if (!params.value) return "-";

       const values = params.value
         .split(",")
         .map((v) => v.trim())
         .filter(Boolean);

       if (values.length === 0) return "-";

       const visible = fileNameExpanded
         ? values.slice(0, 1)
         : values.slice(0, 2);

       const remaining = fileNameExpanded ? values.slice(1) : values.slice(2);

       return (
         <div className="category-icon-cell">
           {visible.map((item) => {
             const cat = CATEGORY_LOOKUP[item];

             return categoryIcons[item] ? (
               <Tooltip key={item} title={cat?.label || item}>
                 <img
                   className="categoryIconsDataTag"
                   src={categoryIcons[item]}
                   alt={item}
                 />
               </Tooltip>
             ) : null;
           })}

           {remaining.length > 0 && (
             <Tooltip
               title={
                 <div className="regulation-tooltip-content">
                   {remaining.map((item) => (
                     <div key={item} className="tooltip-row category">
                       {categoryIcons[item] && (
                         <img
                           src={categoryIcons[item]}
                           className="categoryIconsDataTag"
                           alt={item}
                         />
                       )}
                       {CATEGORY_LOOKUP[item]?.label || item}
                     </div>
                   ))}
                 </div>
               }
               placement="top"
               arrow
               classes={{
                 tooltip: "custom-tooltip",
                 arrow: "custom-tooltip-arrow",
               }}
             >
               <span className="regulation-more">+{remaining.length}</span>
             </Tooltip>
           )}
         </div>
       );
     },
   },

   ...(!isDataBase
     ? [
         {
           field: "fileType",
           headerName: "File Type",
           hideable: false,
           flex: 0.7,
           headerAlign: "center",
           align: "center",
           renderCell: (p) => safe(p.value),
         },
         {
           field: "fieldSize",
           headerName: "File Size",
           hideable: false,
           flex: 1,
           headerAlign: "center",
           align: "center",
           renderCell: (p) => safe(p.value),
           sortComparator: (v1, v2) => sizeToBytes(v1) - sizeToBytes(v2),
         },
         {
           field: "duplicateCount",
           headerName: "Dups",
           hideable: false,
           flex: 0.7,
           headerAlign: "center",
           align: "center",
           renderCell: (p) => renderDuplicateCounts(p.value),
         },
       ]
     : []),

   {
     field: "lastScanTime",
     headerName: "Last Scan",
     hideable: false,
     flex: 1.2,
     renderCell: (p) => formatTime(p.value),
   },
   {
     field: "lastAccessTime",
     headerName: "Last Accessed",
     flex: 1,
     renderCell: (p) => formatTime(p.value),
   },
   {
     field: "tagStatus",
     headerName: "Tag Status",
     hideable: false,
     flex: 0.8,
     headerAlign: "center",
     align: "center",
     renderCell: (p) => renderTagStatus(p.value),
   },
 ];

  const handleSelect = (user) => {
    console.log(user, "vhxzs");
    // selectedFile(user);
  };

  return (
    <div className="dt-all-files-table">
      <QuickFilters
        showCategory={summaryDataRaw.length > 0}
        categoryValue={categoryFilter}
        onCategoryChange={setSelectedCategoryFilter}
        sensitivityValue={selectedSensitivity}
        onSensitivityChange={setSelectedSensitivity}
        regulatedValue={regulatedFilter}
        onRegulatedChange={toggleFilter}
        filters={filters}
        typeFilterList={fileTypesList}
        typeFilter={typeFilter}
        onTypeFilterValue={setFileTypeFilter}
        extensionFilter={extensionFilter}
        onExtensionFilter={setExtensionFilter}
      />

      {selectMode && (
        <div className="tag-select-banner">
          <span>
            <b>{selectedFileIds.length}</b>{" "}
            {selectedFileIds.length === 1 ? "file" : "files"} selected. Search,
            change the page size and tick the files to tag, then click Done.
          </span>
          {selectedFileIds.length > 0 && (
            <button type="button" onClick={() => setSelectedFileIds([])}>
              Clear
            </button>
          )}
        </div>
      )}

      <DataGrid
        rows={tableData.map((r, index) => ({ id: index + 1, ...r }))}
        columns={columns}
        className="mui-table"
        rowCount={totalCount}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        onRowClick={handleFilePopUp}
        disableColumnFilter
        disableColumnSelector={false}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        getTogglableColumns={(columns) =>
          columns
            .filter((col) => col.field === "lastAccessTime")
            .map((col) => col.field)
        }
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden",
          },
        }}
      />

      <Drawer
        classes={{ paper: classes.paper }}
        anchor="right"
        open={dataMapFilePopUp}
        onClose={() => dispatch({ type: "CLOSE_FILE_POP_UP" })}
      >
        <RenderDataMapFilePopUp sourceName={sourceName} />
        {selectedFile  && !isDataBase && (
          <div>
            <hr className="tag-separatorStyle"></hr>
            <div className="copiesContainer">
              <div className="title">Copies:</div>
              <div
                style={{
                  height: "400px",
                  padding: "12px 16px",
                  width: "560px",
                }}
              >
                <DuplicateFileDrillDown
                  selectedFile={selectedFile}
                  onBack={() => setSelectedFile(null)}
                  loc="filesTable"
                />
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}